import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  UntypedFormControl,
  NG_VALUE_ACCESSOR,
  Validators,
} from '@angular/forms';
import { Observable, Subscription, tap, debounceTime, filter, finalize, switchMap } from 'rxjs';
import {
  clearField,
  getError,
  filterInputValue,
  getWarehouseDescription,
  warehouseResultMaskValidator,
  PostboxErrors,
} from '@shared';
import { ECategoryOfWarehouse, WarehouseResult } from '@models';
import { AddressService } from '@services';
import {
  PostboxControlName,
  PostboxLabel,
  PostboxMaxLength,
  PostboxMinLength,
  PostboxPlaceholder,
  RequestLimit,
  PostboxClasses,
  PostboxDebounceTime,
} from './postbox-select.constants';

@Component({
  selector: 'app-postbox-select',
  templateUrl: './postbox-select.component.html',
  styleUrls: ['./postbox-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PostboxSelectComponent),
      multi: true,
    },
  ],
})
export class PostboxSelectComponent implements ControlValueAccessor, OnInit, OnDestroy {
  protected readonly PostboxErrors = PostboxErrors;
  protected readonly getError = getError;
  protected readonly clearField = clearField;
  protected readonly getPostboxDescription = getWarehouseDescription;

  private changeFn: (_: any) => void;
  private touchFn: () => void;

  private postboxSearchSubscription: Subscription = null;
  private currentCityRef: string = '';
  postboxes: WarehouseResult[] = [];
  loading: boolean = false;

  @ViewChild('input', { read: ElementRef, static: false }) input: ElementRef;

  @Input() formControl: UntypedFormControl = new UntypedFormControl('', [
    warehouseResultMaskValidator(),
    Validators.required,
  ]);

  @Input() forSender: boolean = false;

  @Input() placeholder: string = PostboxPlaceholder;

  @Input() label: string = PostboxLabel;

  @Input() testId: string = PostboxControlName;

  @Input() minLength: number = PostboxMinLength;

  @Input() maxLength: number = PostboxMaxLength;

  @Input() requestLimit: number = RequestLimit;

  @Input() debounceTime: number = PostboxDebounceTime;

  @Input() clearEnabled: boolean = true;

  @Input()
  @HostBinding('class')
  classList = PostboxClasses;

  @Input() itemsFilterConfig: ((warehouse: WarehouseResult) => WarehouseResult) | null = null;

  @Input() set cityRef(value: string) {
    this.currentCityRef = value;

    this.resetPostboxes();
  }

  get postbox(): AbstractControl {
    return this.formControl;
  }

  constructor(protected addressService: AddressService, private cd: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.initPostboxSearchSubscription();
  }

  private initPostboxSearchSubscription(): void {
    if (this.postboxSearchSubscription) {
      this.postboxSearchSubscription.unsubscribe();
    }

    this.postboxSearchSubscription = this.postbox?.valueChanges
      .pipe(
        tap((value) => {
          if (value.length === 0) {
            this.postboxes = [];
          }
        }),
        filter((value) => !!value && filterInputValue(value, this.minLength)),
        filter(() => !!this.currentCityRef),
        debounceTime(this.debounceTime),
        switchMap((query) => this.getWarehouses(query)),
      )
      .subscribe((postboxes) => {
        this.setMappedPostboxes(postboxes);
        this.cd.markForCheck();
        this.focus();
      });
  }

  private setMappedPostboxes(postboxes: WarehouseResult[]): void {
    this.postboxes = postboxes.map((postbox) => (this.itemsFilterConfig ? this.itemsFilterConfig(postbox) : postbox));
  }

  private resetPostboxes(): void {
    this.postbox.reset('', { emitEvent: false });
    this.postboxes = [];
    this.setLoading(false);

    // Reinitialization is needed so that when the city changes,
    // it doesn't make a request using the previous input value
    this.initPostboxSearchSubscription();
  }

  private getWarehouses(searchQuery: string): Observable<WarehouseResult[]> {
    this.setLoading(true);

    return this.addressService
      .getWarehouses(this.currentCityRef, null, undefined, this.forSender, ECategoryOfWarehouse.Postomat, searchQuery)
      .pipe(finalize(() => this.setLoading(false)));
  }

  private focus(): void {
    this.input?.nativeElement.focus();
  }

  setLoading(value: boolean): void {
    this.loading = value;
    this.cd.markForCheck();
  }

  blur(): void {
    this.input.nativeElement.blur();
  }

  writeValue(obj: any): void {
    if (obj === this.formControl.value) {
      return;
    }
    this.formControl.setValue(obj);
  }

  registerOnChange(fn: any): void {
    this.changeFn = fn;
  }

  registerOnTouched(fn: any): void {
    this.touchFn = fn;
  }

  ngOnDestroy(): void {
    this.postboxSearchSubscription?.unsubscribe();
  }
}
